/*
 * PROD-7828 R6 — the Penalties tab's mirror of goserver's frozen penalties contract (penalties/types.go,
 * enums.go) plus the pure validation the forms run before sending. params.test.js pins every constant
 * against __fixtures__/penalty_enums.json, so a regenerated golden that changes a code, a label or a
 * limit fails the suite instead of the tab. Months on the wire are "YYYY-MM" (the shell's form).
 */

export const SOURCE = { SYSTEM: 'SYSTEM', MANUAL: 'MANUAL' };

export const WAIVER_STATUS = {
  NOT_WAIVED: 'NOT_WAIVED',
  WAIVED: 'WAIVED',
  FREE_PASS_APPLIED: 'FREE_PASS_APPLIED'
};

// A month-level penalty (overpayment recovery, D64): no session, amount only.
export const MANUAL_TYPE = 'MANUAL';

export const ROW_ACTION = {
  CORRECT: 'correct',
  WAIVE: 'waive',
  UNWAIVE: 'unwaive',
  REVERT: 'revert',
  REMOVE: 'remove',
  APPLY_PASS: 'apply_pass',
  REMOVE_PASS: 'remove_pass'
};

export const BULK_ACTION = { WAIVE: 'WAIVE', APPLY_FREE_PASS: 'APPLY_FREE_PASS' };
export const GROUP_OUTCOME = { APPLIED: 'APPLIED', REFUSED: 'REFUSED' };
export const ITEM_OUTCOME = {
  APPLIED: 'APPLIED',
  UNCHANGED: 'UNCHANGED',
  REFUSED: 'REFUSED',
  GROUP_REFUSED: 'GROUP_REFUSED'
};

export const DIRECTIVE_ACTION = { REMOVE: 'REMOVE', APPLY: 'APPLY', RESET_AUTO: 'RESET_AUTO' };
export const DIRECTIVE_CHOICE = { MOVE: 'MOVE', KEEP_UNUSED: 'KEEP_UNUSED' };
// CD-219: each free-pass day's own offered actions, as the server computes them (pins/held/balance).
export const DAY_ACTION = { USE_PASS: 'use_pass', REMOVE_PASS: 'remove_pass' };
export const PLACED_SOURCE = { AUTO: 'AUTO', PIN: 'PIN' };
export const PASS_STATE = {
  PLACED: 'PLACED',
  HELD_PREV_MONTH_OPEN: 'HELD_PREV_MONTH_OPEN',
  HELD_LATER_MONTH_CLOSED: 'HELD_LATER_MONTH_CLOSED',
  NO_BALANCE: 'NO_BALANCE',
  NO_ELIGIBLE_DAY: 'NO_ELIGIBLE_DAY'
};

// overridden / orphaned list filters take "true" / "false"; "" sends no filter.
export const FILTER_BOOL = { TRUE: 'true', FALSE: 'false' };

// RESCHEDULE_ABUSE is no longer produced by the engine (replaced by the 4 CANCEL_RS_*/REPLACE_RS_*
// types below) but still exists on old rows, so it stays labelled and free-pass eligible.
export const FREE_PASS_ELIGIBLE_TYPES = [
  'CANCEL_24_1',
  'REPLACE_24_1',
  'RESCHEDULE_ABUSE',
  'CANCEL_RS_24_1',
  'REPLACE_RS_24_1'
];

export const PENALTY_TYPES = [
  { penalty_type: 'TEACHER_NO_SHOW', label: 'Teacher no-show' },
  { penalty_type: 'LATE_JOIN', label: 'Late join' },
  { penalty_type: 'AV_LATE', label: 'Audio/video late' },
  { penalty_type: 'EARLY_EXIT', label: 'Early exit' },
  { penalty_type: 'CANCEL_1', label: 'Cancellation <1 hr' },
  { penalty_type: 'CANCEL_24_1', label: 'Cancellation <24–1 hr' },
  { penalty_type: 'REPLACE_1', label: 'Replacement <1 hr' },
  { penalty_type: 'REPLACE_24_1', label: 'Replacement <24–1 hr' },
  { penalty_type: 'RESCHEDULE_ABUSE', label: 'Reschedule abuse' },
  { penalty_type: 'CANCEL_RS_1', label: 'Cancellation of rescheduled session <1 hr' },
  { penalty_type: 'CANCEL_RS_24_1', label: 'Cancellation of rescheduled session <24–1 hr' },
  { penalty_type: 'REPLACE_RS_1', label: 'Replacement of rescheduled session <1 hr' },
  { penalty_type: 'REPLACE_RS_24_1', label: 'Replacement of rescheduled session <24–1 hr' },
  { penalty_type: MANUAL_TYPE, label: 'Manual (month-level)' }
];

export const type_label = (type) =>
  (PENALTY_TYPES.find((t) => t.penalty_type === type) || {}).label || type || '';

// Session-linked types only; MANUAL is the month-level add.
export const SESSION_TYPES = PENALTY_TYPES.filter((t) => t.penalty_type !== MANUAL_TYPE);

// D81 split: a correction, a waiver and an add each take their own list; bulk WAIVE uses the waiver list.
export const REASON_CODES = {
  correct: ['SYSTEM_DETECTION_ERROR', 'WRONG_PENALTY_TYPE', 'TECHNICAL_ISSUE', 'OTHER'],
  waive: ['TEACHER_QUERY_APPROVED', 'TECHNICAL_ISSUE', 'GOODWILL_EXCEPTION', 'OTHER'],
  add: ['MISSED_BY_SYSTEM', 'AV_LATE_OBSERVED', 'OVERPAYMENT_RECOVERY', 'OTHER']
};

export const REASON_LABELS = {
  AV_LATE_OBSERVED: 'Audio/video late observed',
  GOODWILL_EXCEPTION: 'Goodwill exception',
  MISSED_BY_SYSTEM: 'Missed by the system',
  OTHER: 'Other',
  OVERPAYMENT_RECOVERY: 'Overpayment recovery',
  SYSTEM_DETECTION_ERROR: 'System detection error',
  TEACHER_QUERY_APPROVED: 'Teacher query approved',
  TECHNICAL_ISSUE: 'Technical issue',
  WRONG_PENALTY_TYPE: 'Wrong penalty type'
};

export const reason_options = (kind) =>
  (REASON_CODES[kind] || []).map((code) => ({ value: code, label: REASON_LABELS[code] || code }));

export const LIMITS = {
  AMOUNT_MAX_DECIMALS: 2,
  APPROVED_BY_MAX_LEN: 64,
  BULK_MAX_ITEMS: 200,
  LIST_LIMIT_DEFAULT: 100,
  LIST_LIMIT_MAX: 500,
  NOTE_MAX_LEN: 1024,
  NOTE_MIN_LEN: 3,
  PCT_AMOUNT_EPSILON: 0.01,
  PCT_MAX: 150,
  PCT_MAX_DECIMALS: 2
};

export const ERROR = {
  INVALID_REQUEST: 'INVALID_REQUEST',
  INVALID_NOTE: 'INVALID_NOTE',
  INVALID_PCT: 'INVALID_PCT',
  INVALID_AMOUNT: 'INVALID_AMOUNT',
  PCT_AMOUNT_MISMATCH: 'PCT_AMOUNT_MISMATCH',
  INVALID_PENALTY_TYPE: 'INVALID_PENALTY_TYPE',
  INVALID_REASON_CODE: 'INVALID_REASON_CODE',
  INVALID_APPROVED_BY: 'INVALID_APPROVED_BY',
  MIXED_EDIT: 'MIXED_EDIT',
  SESSION_REQUIRED: 'SESSION_REQUIRED',
  BULK_TOO_LARGE: 'BULK_TOO_LARGE',
  INVALID_DIRECTIVE: 'INVALID_DIRECTIVE',
  FORBIDDEN: 'FORBIDDEN',
  PENALTY_NOT_FOUND: 'PENALTY_NOT_FOUND',
  SESSION_NOT_FOUND: 'SESSION_NOT_FOUND',
  TEACHER_NOT_FOUND: 'TEACHER_NOT_FOUND',
  MONTH_CLOSED: 'MONTH_CLOSED',
  MONTH_NOT_OPEN: 'MONTH_NOT_OPEN',
  MONTH_OUTSIDE_WINDOW: 'MONTH_OUTSIDE_WINDOW',
  PREVIOUS_MONTH_NO_RECORD: 'PREVIOUS_MONTH_NO_RECORD',
  VERSION_CONFLICT: 'VERSION_CONFLICT',
  PENALTY_PASS_COVERED: 'PENALTY_PASS_COVERED',
  PENALTY_ORPHANED: 'PENALTY_ORPHANED',
  NOT_OVERRIDDEN: 'NOT_OVERRIDDEN',
  MANUAL_NOT_REVERTIBLE: 'MANUAL_NOT_REVERTIBLE',
  SYSTEM_NOT_REMOVABLE: 'SYSTEM_NOT_REMOVABLE',
  SESSION_NOT_OWNED: 'SESSION_NOT_OWNED',
  FREE_PASS_NOT_ON_DAY: 'FREE_PASS_NOT_ON_DAY',
  FREE_PASS_DAY_NOT_ELIGIBLE: 'FREE_PASS_DAY_NOT_ELIGIBLE',
  FREE_PASS_ALREADY_ON_DAY: 'FREE_PASS_ALREADY_ON_DAY',
  FREE_PASS_NO_BALANCE: 'FREE_PASS_NO_BALANCE',
  CONFIRM_DISPLACE_REQUIRED: 'CONFIRM_DISPLACE_REQUIRED',
  DIRECTIVE_CHANGED: 'DIRECTIVE_CHANGED',
  CONFIRM_LARGE_REQUIRED: 'CONFIRM_LARGE_REQUIRED',
  POSSIBLE_DUPLICATE: 'POSSIBLE_DUPLICATE',
  IDEMPOTENCY_MISMATCH: 'IDEMPOTENCY_MISMATCH',
  BAD_SORT: 'BAD_SORT',
  LOOKUP_FAILED: 'LOOKUP_FAILED'
};

// GET /penalties/list column sort (penalties_list_sort.go penaltyListSort); "" = the default id keyset.
export const LIST_SORT_KEYS = [
  'teacher_name',
  'session_time',
  'student_name',
  'type',
  'base_rate',
  'pct',
  'original',
  'charged',
  'status'
];
export const SORT_DIRS = ['asc', 'desc'];

export const WARNING = {
  RECOMPUTE_FAILED: 'RECOMPUTE_FAILED',
  TEACHER_HAS_NO_BASE_RATE: 'TEACHER_HAS_NO_BASE_RATE',
  MONTH_WILL_GO_NEGATIVE: 'MONTH_WILL_GO_NEGATIVE',
  PIN_DISPLACES_AUTO: 'PIN_DISPLACES_AUTO',
  PASS_COVERS_NEW_PENALTY: 'PASS_COVERS_NEW_PENALTY'
};

// ---- pure validation (the server re-validates; these keep a 400 from being the first feedback) ----

const text = (raw) => String(raw === null || raw === undefined ? '' : raw);

// Code points, as the server counts runes (HL8), never UTF-16 units.
export const char_length = (raw) => Array.from(text(raw).trim()).length;

export const note_error = (raw) => {
  const n = char_length(raw);
  if (n < LIMITS.NOTE_MIN_LEN) return `Enter a note of at least ${LIMITS.NOTE_MIN_LEN} characters.`;
  if (n > LIMITS.NOTE_MAX_LEN) return `The note must be at most ${LIMITS.NOTE_MAX_LEN} characters.`;
  return '';
};

export const approved_by_error = (raw) =>
  char_length(raw) > LIMITS.APPROVED_BY_MAX_LEN
    ? `"Approved by" must be at most ${LIMITS.APPROVED_BY_MAX_LEN} characters.`
    : '';

const DECIMAL = /^\d+(\.\d+)?$/;
const decimals = (s) => (s.indexOf('.') === -1 ? 0 : s.length - s.indexOf('.') - 1);

// A typed number, or null when blank or not a plain non-negative decimal.
export const parse_number = (raw) => {
  const s = text(raw).trim();
  return DECIMAL.test(s) ? Number(s) : null;
};

export const pct_error = (raw) => {
  const s = text(raw).trim();
  const n = parse_number(s);
  if (n === null || n > LIMITS.PCT_MAX || decimals(s) > LIMITS.PCT_MAX_DECIMALS) {
    return `Enter a percentage from 0 to ${LIMITS.PCT_MAX}, with at most ${LIMITS.PCT_MAX_DECIMALS} decimals.`;
  }
  return '';
};

export const amount_error = (raw) => {
  const s = text(raw).trim();
  const n = parse_number(s);
  if (n === null || decimals(s) > LIMITS.AMOUNT_MAX_DECIMALS) {
    return `Enter an amount in rupees with at most ${LIMITS.AMOUNT_MAX_DECIMALS} decimals.`;
  }
  return '';
};

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

// The §9.3 basis turns a percentage into rupees and back; the server holds the same rule.
export const amount_from_pct = (basis, pct) =>
  basis > 0 && pct !== null ? round2((basis * pct) / 100) : null;

export const pct_from_amount = (basis, amount) =>
  basis > 0 && amount !== null ? round2((amount / basis) * 100) : null;

export const is_pass_eligible = (type) => FREE_PASS_ELIGIBLE_TYPES.indexOf(type) !== -1;

// CD-217: default_pcts is the live map from GET /penalties/summary (policy_key/default_pct per type),
// never a build-time mirror. Missing/unloaded summary or a null policy value both leave the box empty.
export const default_pct_text = (type, default_pcts) => {
  const pct = (default_pcts || {})[type];
  return pct === null || pct === undefined ? '' : String(pct);
};

// A type change re-fills only an empty box or the previous type's default, never a figure Ops typed.
export const prefill_pct = (current, prev_type, next_type, default_pcts) =>
  text(current) === '' || text(current) === default_pct_text(prev_type, default_pcts)
    ? default_pct_text(next_type, default_pcts)
    : text(current);

// A confirm answers only the body the server refused; any edit since makes the next send a fresh one.
export const unchanged_since = (sent, now) =>
  !!sent && JSON.stringify(sent) === JSON.stringify(now);
